import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { AuthConnection, AuthDivider, AuthHandoff, AuthHead, AuthMark, AuthNotice, AuthOption, AuthPage } from '../shared/sso-workspace/auth-card'
import { ProviderLogo } from '../shared/sso-workspace/provider-logo'
import { ErrorReference } from '../shared/error/error-reference'
import { requestIDOf } from '../shared/error/request-id'
import { COPY, expiresText, waitText } from './open-link-confirm-copy'

// The held sign-in's confirm page's screens (SSO link confirmation, plan
// 5.1). Each draws what use-link-confirm.js decided (`model`) and calls
// its steps; none decides anything itself.
//
//   WaysScreen     A: the password, the methods the account has, the code
//   CodeScreen     B: the emailed code, a new one, another way
//   NoWayCard      nothing left to confirm with here
//   MethodHandoff  on the way to a method's sign-in

const START_AGAIN = '/auth/email'

const Email = ({ value }) =>
  value ? <span className="sa-email sw-mono" data-testid="sa-email">{value}</span> : <>this email address</>

// A refusal or outcome under the page's head: role=alert, with "Back to
// sign in" when the way on is to start again, and the reference of a
// failure on our side.
export const Notice = ({ notice }) =>
  notice
    ? <AuthNotice tone={notice.tone || 'bad'} role="alert">
        <p>{notice.text}</p>
        {notice.restart &&
          <div className="sa-inline-actions">
            <Link className="btn btn-sm sw-btn" to={START_AGAIN}>Back to sign in</Link>
          </div>}
        {notice.failure && <ErrorReference error={notice.failure} />}
      </AuthNotice>
    : null

const Connection = ({ provider }) =>
  <AuthConnection prefix="Signing in with" providerLink={provider && provider.displayName ? provider : null} />

const ResendHint = ({ seconds }) =>
  seconds > 0 ? <p className="sw-hint m-0" id="sa-resend-hint">You can ask for a new code in {waitText(seconds)}.</p> : null

export const NoWayCard = ({ model, state, onCancel }) =>
  <AuthPage labelledBy="link-none-title">
    <AuthHead id="link-none-title" title="We can't confirm this account here" mark={<AuthMark tone="warn">!</AuthMark>} />
    {model.noWayLead && <Notice notice={model.noWayLead} />}
    <p className="m-0">{model.noWayBody}</p>
    <ErrorReference requestID={requestIDOf(model.failure) || requestIDOf(state)} />
    <div className="sa-actions">
      <Link className="btn sw-btn-primary sa-block" to={START_AGAIN}>Back to sign in</Link>
      <button type="button" className="btn sw-btn sa-block" onClick={onCancel}>Cancel</button>
    </div>
  </AuthPage>

export const MethodHandoff = ({ provider, method }) =>
  <AuthPage labelledBy="link-method-title">
    <Connection provider={provider} />
    <AuthHead id="link-method-title" title="Confirm it's your account" />
    <AuthHandoff providerLink={method}>Taking you to {method.displayName}…</AuthHandoff>
  </AuthPage>

export const CodeScreen = ({ model, provider, email, onCancel }) => {
  const { code, codeExpired, canSend, sendWait, locked, invalid, busy, typed, sent, notice } = model
  const anotherWay = model.another('email')

  return (
    <AuthPage labelledBy="link-code-title">
      <Connection provider={provider} />
      <AuthHead
        id="link-code-title"
        title="Check your email"
        lede={<>We sent an 8-digit code to <Email value={email} />. {codeExpired ? 'It has expired.' : model.codeOutstanding ? expiresText(code.expiresAt) : ''}</>}
      />

      {sent && <p className="sa-sent m-0" role="status">{sent}</p>}
      <Notice notice={notice} />

      <form className="sa-form" onSubmit={model.verify} noValidate>
        <div className="sw-field">
          <label className="sw-label" htmlFor="sa-link-code">Code</label>
          <input
            ref={model.codeInput}
            id="sa-link-code"
            name="code"
            className="form-control sw-mono sa-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            spellCheck={false}
            maxLength={12}
            placeholder="1234 5678"
            value={typed}
            disabled={locked.code}
            aria-invalid={invalid.code ? true : undefined}
            onChange={e => model.setTyped(e.target.value)}
          />
        </div>
        <button type="submit" className="btn sw-btn-primary sa-block" disabled={locked.code || busy === 'verify' || !typed.trim()}>
          {busy === 'verify' ? 'Confirming…' : 'Confirm'}
        </button>
      </form>

      <div className="sa-actions">
        {canSend && !locked.code &&
          <button type="button" className="btn sw-btn sa-block" disabled={sendWait > 0 || busy === 'send'}
            aria-describedby={sendWait > 0 ? 'sa-resend-hint' : undefined} onClick={model.send}>
            {busy === 'send' ? 'Sending…' : 'Send a new code'}
          </button>}
        {!locked.code && <ResendHint seconds={sendWait} />}
        {model.ways.email && model.nextSend === null && model.codeOutstanding && !(notice && notice.key === 'no_more_sends') &&
          <p className="sw-hint m-0">{COPY.noMoreSendsCode}</p>}
        {anotherWay
          ? <button type="button" className="btn sw-btn sa-block" onClick={model.toWays}>Use another way</button>
          : <button type="button" className="btn sw-btn sa-block" onClick={onCancel}>Cancel</button>}
      </div>
    </AuthPage>
  )
}

export const WaysScreen = ({ model, provider, email, onCancel }) => {
  const [reveal, setReveal] = useState(false)
  const { ways, locked, invalid, busy, password, notice, canSend, sendWait } = model
  const name = provider.displayName || 'this sign-in method'
  const groups = []

  if (ways.password) {
    groups.push(
      <form key="password" className="sa-form" onSubmit={model.confirmPassword} noValidate>
        {email && <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />}
        <div className="sw-field">
          <label className="sw-label" htmlFor="sa-link-password">Your EducationXR password</label>
          <div className="input-group">
            <input
              ref={model.passwordInput}
              id="sa-link-password"
              name="password"
              className="form-control"
              type={reveal ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              disabled={locked.password}
              aria-invalid={invalid.password ? true : undefined}
              onChange={e => model.setPassword(e.target.value)}
            />
            <button type="button" className="btn sw-btn" aria-controls="sa-link-password" aria-label={reveal ? 'Hide password' : 'Show password'}
              disabled={locked.password} onClick={() => setReveal(!reveal)}>
              {reveal ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        <button type="submit" className="btn sw-btn-primary sa-block" disabled={locked.password || busy === 'password' || !password}>
          {busy === 'password' ? 'Checking…' : 'Confirm with password'}
        </button>
      </form>
    )
  }

  if (ways.linked.length > 0) {
    groups.push(
      <ul key="linked" className="sa-options" aria-label="Sign-in methods your account already has">
        {ways.linked.map(m =>
          <li key={m.identifier}>
            <AuthOption
              logo={<ProviderLogo iconSVG={m.iconSVG} />}
              name={<>Continue with <strong>{m.displayName}</strong></>}
              sub={`Your account can already sign in with ${m.displayName}.`}
              disabled={Boolean(busy)}
              onClick={() => model.startMethod(m)}
            />
          </li>)}
      </ul>
    )
  }

  if (model.codeOutstanding) {
    groups.push(
      <button key="email" type="button" className="btn sw-btn sa-block" onClick={model.toCode}>Enter the code we emailed</button>
    )
  } else if (canSend) {
    groups.push(
      <div key="email" className="sa-actions">
        <button type="button" className="btn sw-btn sa-block" disabled={sendWait > 0 || Boolean(busy)}
          aria-describedby={sendWait > 0 ? 'sa-resend-hint' : undefined} onClick={model.send}>
          {busy === 'send' ? 'Sending…' : 'Email me a code'}
        </button>
        <ResendHint seconds={sendWait} />
      </div>
    )
  }

  return (
    <AuthPage labelledBy="link-confirm-title">
      <Connection provider={provider} />

      <AuthHead
        id="link-confirm-title"
        title="Confirm it's your account"
        lede={<>You already have an EducationXR account for <Email value={email} />. Before {name} can sign you in, show us it's yours. You only do this once.</>}
      />

      <AuthNotice tone="warn">
        <p>Only continue if you were expecting to sign in with {name}.</p>
      </AuthNotice>

      <Notice notice={notice} />

      {groups.map((group, i) =>
        <React.Fragment key={i}>
          {i > 0 && <AuthDivider />}
          {group}
        </React.Fragment>)}

      <button type="button" className="btn sw-btn sa-block" onClick={onCancel}>Cancel</button>
    </AuthPage>
  )
}
